/**
 * SubstitutionsPage — Coverage Board v2
 *
 * - Date navigator with URL sync (?date=YYYY-MM-DD)
 * - Stat chips: absent / pending / covered / published (derived from grid)
 * - Auto-Assign All with per-row progress
 * - Discard All with warning dialog + 3-second undo timer
 * - Publish All with conflict validation (blocks on absent/inactive substitutes)
 * - Default date: tomorrow if it has unposted absences, else earliest unposted date, else today
 * - Mobile: card-based layout with period pills (responsive breakpoint at sm)
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PublishIcon from '@mui/icons-material/Publish';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import { format, addDays, subDays, parseISO, isToday, isTomorrow } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAbsences, triggerEngine, rerunAutoAssignment, postPlan, getDraftAssignments, discardAllDrafts } from '@timetable/api';
import type { AbsenceWithStatus, AutoAssignProgress } from '@timetable/types';
import { useAuthStore } from '@/store/authStore';
import { useToast } from '@/components/Toast';
import { queryKeys } from '@/lib/queryKeys';
import { SubstitutionGrid, type GridStats } from '@/components/substitutions/SubstitutionGrid';
import { AutoAssignProgressDialog, type BulkQueueItem } from '@/components/substitutions/AutoAssignProgressDialog';
import { MobileSubstitutionCard } from '@/components/substitutions/MobileSubstitutionCard';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Duration of the undo countdown in ms */
const DISCARD_UNDO_MS = 3000;
/** How often the countdown ticks in ms */
const DISCARD_TICK_MS = 50;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toISO(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

function dateLabel(iso: string): string {
  const d = parseISO(iso);
  if (isToday(d)) return `Today · ${format(d, 'EEE, MMM d')}`;
  if (isTomorrow(d)) return `Tomorrow · ${format(d, 'EEE, MMM d')}`;
  return format(d, 'EEEE, MMM d, yyyy');
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function SubstitutionsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { role, schoolContext } = useAuthStore();
  const { success: showSuccess, error: showError } = useToast();
  const isAdmin = role === 'admin' || role === 'super_admin';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── Date state — synced with URL ───────────────────────────────────────────
  const urlDate = searchParams.get('date');
  const [selectedDate, setSelectedDate] = useState<string>(urlDate || toISO(new Date()));
  const [defaultDateResolved, setDefaultDateResolved] = useState(!!urlDate);

  // ── Grid / mobile stats ────────────────────────────────────────────────────
  const [gridStats, setGridStats] = useState<GridStats>({ pending: 0, covered: 0, published: 0 });

  // Mobile: per-card stats aggregated here
  const [mobileCardStats, setMobileCardStats] = useState<Map<string, { pending: number; covered: number; published: number }>>(new Map());

  const handleMobileCardStats = useCallback((absenceId: string, pending: number, covered: number, published: number) => {
    setMobileCardStats((prev) => {
      const next = new Map(prev);
      next.set(absenceId, { pending, covered, published });
      return next;
    });
  }, []);

  const mobileAggregatedStats = useMemo<GridStats>(() => {
    let pending = 0, covered = 0, published = 0;
    for (const s of mobileCardStats.values()) {
      pending += s.pending;
      covered += s.covered;
      published += s.published;
    }
    return { pending, covered, published };
  }, [mobileCardStats]);

  const effectiveStats = isMobile ? mobileAggregatedStats : gridStats;

  // ── Publish / conflict dialog ──────────────────────────────────────────────
  const [publishAllRunning, setPublishAllRunning] = useState(false);
  const [conflictDialog, setConflictDialog] = useState<{ open: boolean; conflicts: string[] }>({ open: false, conflicts: [] });

  // Bulk auto-assign progress dialog state
  const [autoAssignAllRunning, setAutoAssignAllRunning] = useState(false);
  const [bulkProgressOpen, setBulkProgressOpen] = useState(false);
  const [bulkProgressRunning, setBulkProgressRunning] = useState(false);
  const [bulkCurrentProgress, setBulkCurrentProgress] = useState<AutoAssignProgress | null>(null);
  const [bulkQueue, setBulkQueue] = useState<BulkQueueItem[]>([]);

  // ── Discard All state ──────────────────────────────────────────────────────
  /** Warning dialog open */
  const [discardWarningOpen, setDiscardWarningOpen] = useState(false);
  /**
   * Countdown phase:
   *   null  = idle (not counting down)
   *   number = ms remaining in the undo window
   */
  const [discardCountdown, setDiscardCountdown] = useState<number | null>(null);
  const [discardRunning, setDiscardRunning] = useState(false);
  const discardTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const discardCancelledRef = useRef(false);

  useEffect(() => {
    if (role === 'teacher') navigate('/dashboard');
  }, [role, navigate]);

  // Sync date to URL
  const navigateToDate = useCallback((iso: string) => {
    setSelectedDate(iso);
    setSearchParams({ date: iso }, { replace: true });
  }, [setSearchParams]);

  // ── Fetch absences for selected date ──────────────────────────────────────
  const { data: absences = [], isLoading, error } = useQuery({
    queryKey: queryKeys.absences.list({ school_id: schoolContext || '', date: selectedDate }),
    queryFn: () => getAbsences({ school_id: schoolContext || '', status: 'all', page: 1, page_size: 100 }),
    select: (data: AbsenceWithStatus[]) =>
      data.filter((a) => a.start_date <= selectedDate && a.end_date >= selectedDate && a.status !== 'cancelled'),
    enabled: !!schoolContext,
  });

  // ── Fetch all absences to determine smart default date ────────────────────
  const { data: allAbsences = [] } = useQuery({
    queryKey: queryKeys.absences.list({ school_id: schoolContext || '', _all: true }),
    queryFn: () => getAbsences({ school_id: schoolContext || '', status: 'all', page: 1, page_size: 200 }),
    enabled: !!schoolContext && !defaultDateResolved,
  });

  // Resolve smart default date once
  useEffect(() => {
    if (defaultDateResolved || urlDate) return;
    if (allAbsences.length === 0) return;

    const tomorrow = toISO(addDays(new Date(), 1));
    const today = toISO(new Date());

    const unposted = allAbsences.filter(
      (a) => a.status !== 'cancelled' && a.status !== 'posted'
    );

    const tomorrowHasUnposted = unposted.some(
      (a) => a.start_date <= tomorrow && a.end_date >= tomorrow
    );

    if (tomorrowHasUnposted) {
      navigateToDate(tomorrow);
      setDefaultDateResolved(true);
      return;
    }

    const futureDates = unposted
      .flatMap((a) => {
        const dates: string[] = [];
        let d = parseISO(a.start_date < today ? today : a.start_date);
        const end = parseISO(a.end_date);
        while (d <= end) {
          dates.push(toISO(d));
          d = addDays(d, 1);
        }
        return dates;
      })
      .filter((d) => d >= today)
      .sort();

    if (futureDates.length > 0) {
      navigateToDate(futureDates[0]);
    }
    setDefaultDateResolved(true);
  }, [allAbsences, defaultDateResolved, urlDate, navigateToDate]);

  const absentTeacherIds = useMemo(() => new Set(absences.map((a) => a.teacher_id)), [absences]);

  // ── Date navigation ────────────────────────────────────────────────────────
  const goToPrev = () => navigateToDate(toISO(subDays(parseISO(selectedDate), 1)));
  const goToNext = () => navigateToDate(toISO(addDays(parseISO(selectedDate), 1)));

  // ── Auto-Assign All ────────────────────────────────────────────────────────
  const handleAutoAssignAll = async () => {
    if (!schoolContext) return;

    // Include all non-cancelled, non-posted absences — including partial drafts.
    // triggerEngine handles fresh runs; rerunAutoAssignment fills gaps when drafts exist.
    const pending = absences.filter((a) => a.status !== 'cancelled' && a.status !== 'posted');

    if (pending.length === 0) return;

    const initialQueue: BulkQueueItem[] = pending.map((a) => ({
      absenceId: a.id,
      teacherName: a.teacher.name,
      status: 'pending',
    }));
    setBulkQueue(initialQueue);
    setBulkCurrentProgress(null);
    setBulkProgressOpen(true);
    setBulkProgressRunning(true);
    setAutoAssignAllRunning(true);

    try {
      // Shared across all absence runs — prevents double-booking the same teacher
      // at the same period when multiple absences overlap in time
      const sharedDraftBatchAssignedPeriods = new Map<string, Array<{ time_start: string; time_end: string }>>();

      for (let i = 0; i < pending.length; i++) {
        const absence = pending[i];

        setBulkQueue((q) =>
          q.map((item) =>
            item.absenceId === absence.id ? { ...item, status: 'running' } : item
          )
        );
        setBulkCurrentProgress(null);

        try {
          const result = await triggerEngine(absence.id, selectedDate, (progress) => {
            setBulkCurrentProgress(progress);
          }, sharedDraftBatchAssignedPeriods);

          let rerunResult: Awaited<ReturnType<typeof rerunAutoAssignment>> | null = null;

          // If a draft already exists, fill remaining gaps via rerun.
          if (result.skipped_reason === 'draft_exists') {
            setBulkQueue((q) =>
              q.map((item) =>
                item.absenceId === absence.id
                  ? { ...item, status: 'running', skippedReason: undefined }
                  : item
              )
            );
            setBulkCurrentProgress({
              phase: 'matching',
              message: `Filling remaining gaps for ${absence.teacher.name}…`,
              absentTeacherName: absence.teacher.name,
              periodsFound: 0,
              periodStates: [],
              periodsAssigned: 0,
              periodsUnassigned: 0,
            });
            rerunResult = await rerunAutoAssignment(absence.id, selectedDate);
            setBulkCurrentProgress({
              phase: 'complete',
              message: rerunResult.periods_assigned > 0
                ? `${rerunResult.periods_assigned} gap${rerunResult.periods_assigned !== 1 ? 's' : ''} filled for ${absence.teacher.name}`
                : `No additional teachers available for ${absence.teacher.name}`,
              absentTeacherName: absence.teacher.name,
              periodsFound: rerunResult.periods_affected,
              periodStates: [],
              periodsAssigned: rerunResult.periods_assigned,
              periodsUnassigned: rerunResult.periods_unassigned,
              elapsedMs: 0,
            });
            // Wait for MIN_PHASE_MS (600ms) so the dialog transitions to Done
            // before setBulkProgressRunning(false) fires in finally.
            // Need 2x MIN_PHASE_MS since matching→complete is two phase transitions.
            await new Promise((r) => setTimeout(r, 1400));
          }

          setBulkQueue((q) =>
            q.map((item) =>
              item.absenceId === absence.id
                ? {
                    ...item,
                    status: 'done',
                    periodsAssigned: rerunResult
                      ? rerunResult.periods_assigned
                      : result.periods_assigned,
                    periodsTotal: rerunResult
                      ? rerunResult.periods_affected
                      : result.periods_affected,
                    skippedReason: undefined,
                  }
                : item
            )
          );
        } catch {
          setBulkQueue((q) =>
            q.map((item) =>
              item.absenceId === absence.id ? { ...item, status: 'error' } : item
            )
          );
        }
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.absences.all });
    } finally {
      setBulkProgressRunning(false);
      setAutoAssignAllRunning(false);
    }
  };

  // ── Discard All — warning → countdown → execute ────────────────────────────

  /** Step 1: User clicks "Discard All" — show warning dialog */
  const handleDiscardAllClick = () => {
    setDiscardWarningOpen(true);
  };

  /** Step 2: User confirms in warning dialog — start 3-second countdown */
  const handleDiscardConfirm = () => {
    setDiscardWarningOpen(false);
    discardCancelledRef.current = false;
    setDiscardCountdown(DISCARD_UNDO_MS);

    // Tick the countdown every DISCARD_TICK_MS
    discardTimerRef.current = setInterval(() => {
      setDiscardCountdown((prev) => {
        if (prev === null) return null;
        const next = prev - DISCARD_TICK_MS;
        if (next <= 0) {
          // Timer expired — execute the discard
          clearInterval(discardTimerRef.current!);
          discardTimerRef.current = null;
          if (!discardCancelledRef.current) {
            executeDiscardAll();
          }
          return null;
        }
        return next;
      });
    }, DISCARD_TICK_MS);
  };

  /** Step 2b: User taps "Tap to cancel" during countdown */
  const handleDiscardCancel = () => {
    discardCancelledRef.current = true;
    if (discardTimerRef.current) {
      clearInterval(discardTimerRef.current);
      discardTimerRef.current = null;
    }
    setDiscardCountdown(null);
  };

  /** Step 3: Execute the actual discard after countdown completes */
  const executeDiscardAll = async () => {
    if (!schoolContext) return;
    setDiscardRunning(true);
    try {
      const result = await discardAllDrafts(schoolContext, selectedDate);
      queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.absences.all });
      if (result.discarded > 0) {
        showSuccess(`Discarded ${result.discarded} draft assignment${result.discarded !== 1 ? 's' : ''}`);
      } else {
        showSuccess('No draft assignments to discard');
      }
    } catch (err: any) {
      showError(err.message || 'Failed to discard drafts');
    } finally {
      setDiscardRunning(false);
    }
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (discardTimerRef.current) clearInterval(discardTimerRef.current);
    };
  }, []);

  // ── Publish All — with conflict validation ─────────────────────────────────
  const handlePublishAllClick = async () => {
    if (!schoolContext) return;

    const conflicts: string[] = [];
    for (const absence of absences) {
      if (absence.status === 'cancelled' || absence.status === 'posted') continue;
      try {
        const assignments = await getDraftAssignments(absence.id, selectedDate);
        for (const a of assignments) {
          if (a.status !== 'draft') continue;
          if (a.substitute_teacher_id && absentTeacherIds.has(a.substitute_teacher_id)) {
            conflicts.push(
              `${absence.teacher.name} · P${a.period.period_number} — ${a.substitute_teacher?.name ?? 'Unknown'} is absent`
            );
          }
          if (a.substitute_teacher && !a.substitute_teacher.is_active) {
            conflicts.push(
              `${absence.teacher.name} · P${a.period.period_number} — ${a.substitute_teacher.name} is inactive`
            );
          }
        }
      } catch { /* skip */ }
    }

    if (conflicts.length > 0) {
      setConflictDialog({ open: true, conflicts });
      return;
    }

    await executePublishAll();
  };

  const executePublishAll = async () => {
    setPublishAllRunning(true);
    try {
      const toPub = absences.filter(
        (a) => a.status === 'active' || a.status === 'draft_pending' || a.status === 'pending'
      );
      let posted = 0;
      const failed: string[] = [];
      for (const absence of toPub) {
        try {
          await postPlan(absence.id, selectedDate);
          posted++;
        } catch (err: any) {
          failed.push(`${absence.teacher.name}: ${err.message || 'failed'}`);
        }
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.absences.all });
      if (failed.length > 0) {
        showError(`Published ${posted}, failed ${failed.length}: ${failed[0]}`);
      } else {
        showSuccess(`Published ${posted} plan${posted !== 1 ? 's' : ''}`);
      }
    } catch (err: any) {
      showError(err.message || 'Publish failed');
    } finally {
      setPublishAllRunning(false);
    }
  };

  // ── Derived state ──────────────────────────────────────────────────────────
  const canPublish = effectiveStats.covered > 0;
  const canAutoAssign = absences.some((a) => a.status !== 'cancelled' && a.status !== 'posted');
  // Discard All is available whenever there are draft assignments (covered > 0 means drafts exist)
  const canDiscardAll = effectiveStats.covered > 0 || effectiveStats.pending > 0;
  const isDiscardCounting = discardCountdown !== null;
  const discardProgress = isDiscardCounting
    ? ((DISCARD_UNDO_MS - discardCountdown!) / DISCARD_UNDO_MS) * 100
    : 0;
  const anyBusy = autoAssignAllRunning || publishAllRunning || discardRunning || isDiscardCounting;

  if (role === 'teacher') return null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="xl" sx={{ py: { xs: 1.5, sm: 3 }, px: { xs: 1.5, sm: 3 } }}>
      {/* ── Toolbar ── */}
      <Paper
        variant="outlined"
        sx={{
          mb: 2,
          bgcolor: 'background.paper',
          borderColor: 'divider',
          borderRadius: { xs: 2, sm: 1 },
          overflow: 'hidden',
        }}
      >
        {/* Row 1: date nav + stat chips + desktop action buttons */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            px: { xs: 1.5, sm: 2.5 },
            py: { xs: 1, sm: 1.5 },
            flexWrap: { xs: 'nowrap', sm: 'wrap' },
          }}
        >
          {/* Date navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <IconButton onClick={goToPrev} size="small" aria-label="previous day">
              <ChevronLeftIcon />
            </IconButton>
            <Typography sx={{ fontSize: { xs: 13, sm: 15 }, fontWeight: 600, minWidth: { xs: 140, sm: 220 }, textAlign: 'center' }}>
              {dateLabel(selectedDate)}
            </Typography>
            <IconButton onClick={goToNext} size="small" aria-label="next day">
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {/* Stat chips — single scrollable row on mobile */}
          <Box
            sx={{
              display: 'flex',
              gap: 0.75,
              alignItems: 'center',
              overflowX: { xs: 'auto', sm: 'visible' },
              flexWrap: { xs: 'nowrap', sm: 'wrap' },
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              flex: { xs: 1, sm: 'none' },
            }}
          >
            <Chip
              label={`${absences.length} absent`}
              size="small"
              sx={{
                flexShrink: 0,
                bgcolor: absences.length > 0 ? '#FEF2F2' : 'action.hover',
                color: absences.length > 0 ? '#991B1B' : 'text.secondary',
                fontWeight: 600, fontSize: 12,
                border: '1px solid', borderColor: absences.length > 0 ? '#FECACA' : 'divider',
              }}
            />
            <Chip
              label={`${effectiveStats.pending} pending`}
              size="small"
              sx={{
                flexShrink: 0,
                bgcolor: effectiveStats.pending > 0 ? '#FFFBEB' : 'action.hover',
                color: effectiveStats.pending > 0 ? '#92400E' : 'text.secondary',
                fontWeight: 600, fontSize: 12,
                border: '1px solid', borderColor: effectiveStats.pending > 0 ? '#FDE68A' : 'divider',
              }}
            />
            <Chip
              label={`${effectiveStats.covered} covered`}
              size="small"
              sx={{
                flexShrink: 0,
                bgcolor: effectiveStats.covered > 0 ? '#F0FDFA' : 'action.hover',
                color: effectiveStats.covered > 0 ? '#0F766E' : 'text.secondary',
                fontWeight: 600, fontSize: 12,
                border: '1px solid', borderColor: effectiveStats.covered > 0 ? '#99F6E4' : 'divider',
              }}
            />
            <Chip
              label={`${effectiveStats.published} published`}
              size="small"
              sx={{
                flexShrink: 0,
                bgcolor: effectiveStats.published > 0 ? '#F0FDF4' : 'action.hover',
                color: effectiveStats.published > 0 ? '#166534' : 'text.secondary',
                fontWeight: 600, fontSize: 12,
                border: '1px solid', borderColor: effectiveStats.published > 0 ? '#BBF7D0' : 'divider',
              }}
            />
          </Box>

          {/* Desktop spacer + action buttons */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, flex: 1 }} />
          {isAdmin && (
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1.5, alignItems: 'center' }}>
              <Tooltip title="Run auto-assignment for all pending absences on this date">
                <span>
                  <Button
                    variant="outlined" color="primary" size="small"
                    startIcon={autoAssignAllRunning ? <CircularProgress size={14} color="inherit" /> : <AutoFixHighIcon />}
                    disabled={anyBusy || !canAutoAssign}
                    onClick={handleAutoAssignAll}
                  >
                    Auto-assign all
                  </Button>
                </span>
              </Tooltip>

              {isDiscardCounting ? (
                <Tooltip title="Tap to cancel discard">
                  <Button
                    variant="outlined" color="error" size="small"
                    onClick={handleDiscardCancel}
                    sx={{ minWidth: 148, position: 'relative', overflow: 'hidden', borderColor: 'error.main', color: 'error.main', '&:hover': { bgcolor: 'error.light' } }}
                  >
                    <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${discardProgress}%`, bgcolor: 'error.light', transition: `width ${DISCARD_TICK_MS}ms linear`, pointerEvents: 'none' }} />
                    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                      <span>Tap to cancel ({Math.ceil(discardCountdown! / 1000)}s)</span>
                    </Box>
                  </Button>
                </Tooltip>
              ) : (
                <Tooltip title="Discard all draft assignments for this date">
                  <span>
                    <Button
                      variant="outlined" color="error" size="small"
                      startIcon={discardRunning ? <CircularProgress size={14} color="inherit" /> : <DeleteSweepIcon />}
                      disabled={anyBusy || !canDiscardAll}
                      onClick={handleDiscardAllClick}
                    >
                      {discardRunning ? 'Discarding…' : 'Discard all'}
                    </Button>
                  </span>
                </Tooltip>
              )}

              <Tooltip title="Publish all draft plans for this date">
                <span>
                  <Button
                    variant="contained" color="success" size="small"
                    startIcon={publishAllRunning ? <CircularProgress size={14} color="inherit" /> : <PublishIcon />}
                    disabled={anyBusy || !canPublish}
                    onClick={handlePublishAllClick}
                  >
                    Publish
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Row 2 (mobile only): compact labelled action buttons */}
        {isAdmin && isMobile && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              px: 1.5,
              pb: 1.25,
              pt: 0,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            {/* Auto-assign all */}
            <Tooltip title="Auto-assign all pending">
              <span style={{ flex: 1 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={autoAssignAllRunning ? <CircularProgress size={13} color="inherit" /> : <AutoFixHighIcon sx={{ fontSize: 16 }} />}
                  disabled={anyBusy || !canAutoAssign}
                  onClick={handleAutoAssignAll}
                  sx={{ fontSize: 12, py: 0.75 }}
                >
                  Auto-assign
                </Button>
              </span>
            </Tooltip>

            {/* Discard all */}
            {isDiscardCounting ? (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleDiscardCancel}
                sx={{ flex: 1, fontSize: 12, py: 0.75, position: 'relative', overflow: 'hidden' }}
              >
                <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${discardProgress}%`, bgcolor: 'error.light', transition: `width ${DISCARD_TICK_MS}ms linear`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                  <span>Cancel ({Math.ceil(discardCountdown! / 1000)}s)</span>
                </Box>
              </Button>
            ) : (
              <Tooltip title="Discard all drafts">
                <span style={{ flex: 1 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={discardRunning ? <CircularProgress size={13} color="inherit" /> : <DeleteSweepIcon sx={{ fontSize: 16 }} />}
                    disabled={anyBusy || !canDiscardAll}
                    onClick={handleDiscardAllClick}
                    sx={{ fontSize: 12, py: 0.75 }}
                  >
                    Discard
                  </Button>
                </span>
              </Tooltip>
            )}

            {/* Publish */}
            <Tooltip title="Publish all drafts">
              <span style={{ flex: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={publishAllRunning ? <CircularProgress size={13} color="inherit" /> : <PublishIcon sx={{ fontSize: 16 }} />}
                  disabled={anyBusy || !canPublish}
                  onClick={handlePublishAllClick}
                  sx={{ fontSize: 12, py: 0.75 }}
                >
                  Publish
                </Button>
              </span>
            </Tooltip>
          </Box>
        )}
      </Paper>

      {/* ── Grid / Mobile Cards ── */}
      <Box>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">Failed to load absences: {(error as Error).message}</Alert>
        ) : isMobile ? (
          /* ── Mobile: card stack ── */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {absences.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  py: 6,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1.5,
                  borderRadius: 3,
                  borderStyle: 'dashed',
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography sx={{ fontSize: 22 }}>✓</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  No absences on this date
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  All teachers are present
                </Typography>
              </Paper>
            ) : (
              absences.map((absence) => (
                <MobileSubstitutionCard
                  key={absence.id}
                  absence={absence}
                  date={selectedDate}
                  absentTeacherIds={absentTeacherIds}
                  isAdmin={isAdmin}
                  onStatsChange={handleMobileCardStats}
                />
              ))
            )}
          </Box>
        ) : (
          /* ── Desktop: frozen-panel grid ── */
          <SubstitutionGrid
            date={selectedDate}
            absences={absences}
            absentTeacherIds={absentTeacherIds}
            onStatsChange={setGridStats}
          />
        )}
      </Box>

      {/* ── Discard All warning dialog ── */}
      <Dialog
        open={discardWarningOpen}
        onClose={() => setDiscardWarningOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <DeleteSweepIcon color="error" />
          <Typography variant="h6" fontWeight="bold">Discard all drafts?</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            This cannot be undone.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            All draft substitution assignments for{' '}
            <strong>{dateLabel(selectedDate)}</strong> will be permanently deleted.
            Published assignments are not affected.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            You will have <strong>3 seconds</strong> to cancel after confirming.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setDiscardWarningOpen(false)}
            sx={{ flex: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDiscardConfirm}
            startIcon={<DeleteSweepIcon />}
            sx={{ flex: 1 }}
          >
            Yes, discard all
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Conflict dialog — blocks publish ── */}
      <Dialog
        open={conflictDialog.open}
        onClose={() => setConflictDialog({ open: false, conflicts: [] })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="error" />
          Conflicts Detected — Cannot Publish
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The following assignments have conflicts that must be resolved before publishing:
          </Typography>
          <List dense disablePadding>
            {conflictDialog.conflicts.map((c, i) => (
              <ListItem key={i} disablePadding sx={{ py: 0.25 }}>
                <ListItemText
                  primary={c}
                  primaryTypographyProps={{ variant: 'body2', color: 'error.main' }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConflictDialog({ open: false, conflicts: [] })}>
            Close &amp; Fix
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk auto-assign progress dialog ── */}
      <AutoAssignProgressDialog
        open={bulkProgressOpen}
        progress={bulkCurrentProgress}
        bulkQueue={bulkQueue}
        isRunning={bulkProgressRunning}
        onClose={() => {
          setBulkProgressOpen(false);
          setBulkCurrentProgress(null);
          setBulkQueue([]);
        }}
      />
    </Container>
  );
}
